/**
 * RoutingCategoriesTab - Gestão de Categorias de Ações Rápidas
 *
 * Listagem com cor/ícone/nome/visibilidade/contador de regras e ações
 * Editar/Excluir. Usado dentro da página /tenant/routing-rules como
 * uma aba dedicada.
 *
 * CHANGELOG:
 *   2026-06-23 v1.0.0 - Versão inicial.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, FolderTree, Lock, Users, Globe } from 'lucide-react';
import {
  useRoutingRuleCategories,
  RoutingRuleCategory,
} from '@/hooks/useRoutingRuleCategories';
import { RoutingCategoryDialog } from './RoutingCategoryDialog';
import { RoutingCategoryIcon } from './RoutingCategoryIcon';

/**
 * Badge visual de visibilidade da categoria.
 */
function VisibilityBadge({ v }: { v: string }) {
  if (v === 'public') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Globe className="h-3 w-3" /> Pública
      </Badge>
    );
  }
  if (v === 'shared') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Users className="h-3 w-3" /> Compartilhada
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <Lock className="h-3 w-3" /> Privada
    </Badge>
  );
}

export function RoutingCategoriesTab() {
  const { categories, isLoading, remove, isRemoving } = useRoutingRuleCategories();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RoutingRuleCategory | null>(null);
  const [toDelete, setToDelete] = useState<RoutingRuleCategory | null>(null);

  const handleEdit = (cat: RoutingRuleCategory) => {
    setEditing(cat);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await remove(toDelete.id);
    } finally {
      setToDelete(null);
    }
  };

  return (
    <>
      <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-md">
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderTree className="h-5 w-5 text-primary" />
                Categorias
              </CardTitle>
              <CardDescription className="mt-1">
                Organize suas Ações Rápidas em grupos visuais. As categorias
                seguem o mesmo modelo de visibilidade (privada, compartilhada
                ou pública) das próprias ações.
              </CardDescription>
            </div>
            <Button size="sm" onClick={handleCreate}>
              <Plus className="mr-1.5 h-4 w-4" />
              Nova Categoria
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <FolderTree className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                Nenhuma categoria criada ainda.
              </p>
              <Button
                variant="link"
                className="mt-1 h-auto p-0 text-primary"
                onClick={handleCreate}
              >
                Criar a primeira categoria
              </Button>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="hidden md:table-cell">Visibilidade</TableHead>
                    <TableHead className="text-center w-24">Ações vinc.</TableHead>
                    <TableHead className="text-right w-32">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell>
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${cat.color}20` }}
                          >
                            <RoutingCategoryIcon
                              name={cat.icon}
                              color={cat.color}
                              size={18}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {cat.name}
                            </p>
                            {cat.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {cat.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <VisibilityBadge v={cat.visibility} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{cat.rules_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {cat.is_owner ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(cat)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setToDelete(cat)}
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">
                              somente leitura
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de criar/editar */}
      <RoutingCategoryDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        category={editing}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.rules_count ? (
                <>
                  Esta categoria está vinculada a{' '}
                  <strong>{toDelete.rules_count}</strong> ação(ões) rápida(s).
                  Ao excluir, essas ações ficarão sem categoria (não serão
                  apagadas). Deseja continuar?
                </>
              ) : (
                'Tem certeza que deseja excluir esta categoria?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
